import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Formik } from '../../lib/formik/Formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showError } from '../../redux-store/actions/auth';
import {
    allocationlimitsType,
    assetallocationguidelinesType,
    investmentpreferencesType,
    NexEdgeInvPrefObj,
    tolerancelimitsType,
    TypeInvPrefNexEdge,
} from '../../redux-store/types/api-types';
import {
    applicationComparison,
    applyRoleBasedStatus,
    isModuleAllowed,
    saveForLater,
} from '../../utils/utilityFunctions';
import { useHistory } from 'react-router';
import { RootStateType } from '../../redux-store/reducers';
import { updateApplication } from '../../redux-store/actions/application';
import { AMC_APPROVER_CHECK_FOR_INDIVIDUAL, ONBOARDING_DEMAT_DETAILS, USER_ROLES } from '../../utils/constant';
import { useSnackbar } from 'notistack';
import { investmentPreferenceMasterData } from '../../redux-store/actions';
import theme from '../../themes';
import { ProceedSaveLater, SubHeading } from '../investors/components';

export type Values = {
    investmentpreferences: investmentpreferencesType[];
    assetallocationguidelines: assetallocationguidelinesType[];
    allocationlimits: allocationlimitsType[];
    tolerancelimits: tolerancelimitsType[];
    saveType: string;
};

export const investmentPreferenceObj: investmentpreferencesType = {
    question: '',
    answer: '',
    value: '',
    section: '',
};

export const assetallocationguidelinesObj: assetallocationguidelinesType = {
    assetClass: '',
    minimumAllocation: 0,
    maximumAllocation: 0,
};

export const allocationlimitsObj: allocationlimitsType = {
    maturityBuckets: '',
    indicativeAllocation: 0,
};

export const tolerancelimitsObj: tolerancelimitsType = {
    fixedIncomeCategory: '',
    allocation: 0,
};

const initialValues: Values = {
    investmentpreferences: [],
    assetallocationguidelines: [],
    allocationlimits: [],
    tolerancelimits: [],
    saveType: 'save and proceed',
};

function InvestmentPreferences({
    invPrefData,
}: {
    invPrefData: NexEdgeInvPrefObj;
}): JSX.Element {

    const assetAllocationData = invPrefData?.asset_allocations;

    return (
        <>
            {invPrefData?.headings?.map((heading, ind) => {
                return (
                    <>
                        <Grid item xs={12} sm={12} lg={12}>
                            <SubHeading
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                {heading?.heading}
                            </SubHeading>
                            <Typography variant='body_500' sx={{ color: `${theme.palette.text.secondary}` }}>
                                {heading?.heading_text}
                            </Typography>
                        </Grid>
                    </>
                );
            })}

            {/* Table */}

            <Grid item xs={12} sx={{ mt: 6 }}>
                <TableContainer>
                    <Table
                        sx={{
                            border: "1px solid #ccc",
                            "& td, & th": {
                                border: "1px solid #ccc",
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">
                                    <Typography variant='h2'>Parameter</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography variant='h2'>Guideline</Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {assetAllocationData?.map((row, index) => {
                                if (row.isHeading || row.isSubHeading) {
                                    return (
                                        <TableRow key={index}>
                                            <TableCell
                                                align={row.isSubHeading ? "left" : "center"}
                                                colSpan={2}
                                                style={{
                                                    fontWeight: "bold",
                                                    backgroundColor: row.isSubHeading ? "unset" : theme.palette.customColors.darkGrey,
                                                }}
                                            >
                                                {row.parameter}
                                            </TableCell>
                                        </TableRow>
                                    );
                                }

                                return (
                                    <TableRow key={index}>
                                        <TableCell align="center">
                                            <Typography variant='body_500'>
                                                {row?.parameter ?? "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {row?.boldGuideline && row?.boldGuideline?.length > 0 && (
                                                <Typography
                                                    variant='body_500'
                                                    sx={{ fontWeight: 'bold', mb: 1 }}
                                                >
                                                    {row?.boldGuideline ?? "-"}
                                                </Typography>
                                            )}
                                            <Typography variant='body_500'>
                                                {row?.guideline ?? "-"}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

        </>
    );
}

export default function NonIndIPS(): JSX.Element {
    const [investmentPreferencesData, setInvestmentPreferencesData] = useState(initialValues);
    const { application } = useSelector((store: RootStateType) => store.application);
    const { role = '' } = useSelector((store: RootStateType) => store.auth);
    const { enqueueSnackbar } = useSnackbar();

    const [invPrefData, setInvPrefData] = useState<NexEdgeInvPrefObj>({} as NexEdgeInvPrefObj);
    const [loading, setLoading] = useState(false);
    const [isApplicationDownloading, setApplicationDownloadStatus] = useState<boolean>(false)
    const dispatch = useDispatch();
    const history = useHistory();

    const riskProfileType = application?.riskProfile?.toLowerCase();

    const handleSubmit = async (values: Values) => {
        try {
            const {
                id,
                applicant1ReferenceId = '',
                currentStep,
                status,
                hasPOA,
                applicationNumber,
            } = application || {};

            const prefDataToBeUsed = invPrefData
                ?.asset_allocations
                ?.filter(item => !item.isHeading)
                ?.filter(item => !item.isSubHeading)
                ?.map(obj => ({
                    ...obj,
                    answer: obj?.guideline ?? "",
                    question: obj?.parameter ?? "",
                    riskProfile: riskProfileType
                })) ?? []

            const { saveType } = values;

            const updatedPayload = {
                ...application,
                investmentpreferences: prefDataToBeUsed,
            };

            const checkApplication = applicationComparison(
                {
                    ...application,
                    investmentpreferences: application?.investmentpreferences,
                },
                {
                    ...updatedPayload,
                    investmentpreferences: updatedPayload.investmentpreferences,
                    currentStep: !!currentStep && currentStep > (ONBOARDING_DEMAT_DETAILS ? 8 : 7) ? currentStep : Number(currentStep) + 1,
                }
            );

            const isSaveLater = saveType !== 'save and proceed';

            let currentStatus = status

            if (
                !isModuleAllowed({ value: (role ?? ""), compareArr: [USER_ROLES.AMCAPPROVER] }) &&
                status === 'sent_to_amc_approver'
            ) {
                currentStatus = 'draft'
            } else if (
                !hasPOA &&
                AMC_APPROVER_CHECK_FOR_INDIVIDUAL &&
                status !== 'draft' &&
                applyRoleBasedStatus(role)
            ) {
                currentStatus = 'sent_to_amc_approver'
            }

            if (id && !checkApplication) {
                setLoading(true);
                const response = await dispatch(
                    updateApplication({
                        body: {
                            ...updatedPayload,
                            status: currentStatus,
                            currentStep: (ONBOARDING_DEMAT_DETAILS ? 9 : 8),
                        },
                        applicationId: id,
                        ...(isSaveLater && { toastMessage: '' }),
                    })
                );
                if (!response) return;
                !isSaveLater
                    // ? history.push('document-details', { id, applicant1ReferenceId })
                    ? history.push('ria-fee', { id, applicant1ReferenceId })
                    : history.push(saveForLater(role, id, applicant1ReferenceId));
            } else if (checkApplication) {
                if (isSaveLater) {
                    enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
                        variant: 'success',
                        autoHideDuration: 3000,
                    });
                }
                !isSaveLater
                    // ? history.push('document-details', { id, applicant1ReferenceId })
                    ? history.push('ria-fee', { id, applicant1ReferenceId })
                    : history.push(saveForLater(role, id, applicant1ReferenceId));
            }
        } catch (e) {
            setLoading(false);
            typeof e === 'string' && dispatch(showError(e));
            console.error((e as Error).message);
        }
    };

    console.log('invPrefData', invPrefData);

    useEffect(() => {
        let isComponentAlive = true;
        (async function () {
            try {
                const { investmentpreferences = [], wishToAddRiskProfile } = application || {};

                const response = (await dispatch(
                    investmentPreferenceMasterData()
                )) as unknown as TypeInvPrefNexEdge;

                if (!isComponentAlive) {
                    return;
                }

                if (investmentpreferences?.length > 0) {
                    setInvPrefData({
                        ...response[riskProfileType as keyof TypeInvPrefNexEdge],
                        ...(wishToAddRiskProfile ? {
                            asset_allocations: (investmentpreferences ?? []).map(obj => ({
                                ...obj,
                                isActive: "0"
                            }))
                        } : {
                            asset_allocations: response[riskProfileType as keyof TypeInvPrefNexEdge]?.asset_allocations?.map(jsonObj => {
                                const existingInvestmentPreferenceObj = investmentpreferences.find(item => ((item?.question ?? "") === (jsonObj?.parameter ?? "")) &&
                                    ((item?.headingKey ?? "") === (jsonObj?.headingKey ?? "")) &&
                                    ((item?.subHeadingKey ?? "") === (jsonObj?.subHeadingKey ?? ""))) ?? {}
                                return {
                                    ...existingInvestmentPreferenceObj,
                                    ...jsonObj,
                                    question: jsonObj?.parameter,
                                    answer: jsonObj?.guideline,

                                }
                            })
                        })
                    });
                } else {
                    setInvPrefData(response[riskProfileType as keyof TypeInvPrefNexEdge]);
                }
            } catch (e) {
                console.error((e as Error).message);
            }
        })();
        return () => {
            isComponentAlive = false;
        };
    }, [application]);

    return (
        <Formik
            initialValues={investmentPreferencesData}
            onSubmit={handleSubmit}
            enableReinitialize={true}>
            {({ handleSubmit, values, setValues }) => (
                <Grid
                    container
                    rowSpacing={1}
                    columnSpacing={3}
                    sx={{
                        width: '100%',
                        ml: 0,
                        // '.MuiGrid-item': { px: { xs: 0, sm: '14px' } },
                    }}
                    component="form"
                    noValidate
                    onSubmit={handleSubmit}>
                    <InvestmentPreferences invPrefData={application?.wishToAddRiskProfile ? {} as NexEdgeInvPrefObj : invPrefData} />
                    <Grid item xs={12} my={2} />
                    <ProceedSaveLater
                        saveLater={() => {
                            setValues({
                                ...values,
                                saveType: 'save for later',
                            });
                        }}
                        saveAndProceed={() => {
                            setValues({
                                ...values,
                                saveType: 'save and proceed',
                            });
                        }}
                        loader={loading}
                        clickedButton={values.saveType}
                        showEndIcon={false}
                        saveButtonText='Save and Proceed'
                        disabled={isApplicationDownloading}
                    />
                </Grid>
            )}
        </Formik>
    );
}
